import { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { PlanogramItem, UnrangedPlanogramItem } from "@CommonTypes/merchflow/pog/item";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import React, { useRef } from "react";
import styled from "styled-components";

import { DragBounds, DragModifications, PogAtom, Scaleable } from "../../store/types";
import { resolveDraggedItems } from "../../store/utils";
import { Draggable } from "../Draggable/Draggable";
import { Dropper } from "../Item/Dropper";
import { DraggedItemsPayload } from "../Item/store/types";
import { CanvasItemLabelRule, Item } from "../Item/Item";
import { onDropOverToShelf } from "../Item/store/utils";
import { Overflowing } from "./Overflowing";
import { ShelfBottom } from "./ShelfBottom";
import { ResponseMerchFlowApplyFilters } from "@CommonApi/merchandise";
import {
  planogramHoveredShelfAtom,
  planogramSelectedShelfAtom,
  showShelfOverflowAtom,
  planogramSelectedShelvesAtom,
} from "src/components/PlanogramExtensions/store/atoms";
import { Flex } from "src/elements";
import { PlanogramBay } from "@CommonTypes/merchflow/pog/bay";
import { usePogAtomActions } from "../../store/actions";
import { calculateShelfSelectionUpdate } from "../../store/utils";
import { usePlanogramPanel } from "../Panel/store/hooks";
import { getMergedMerchandisableWidth } from "@CommonUtils/pog/shelfMerchandising";
import { NonMerchandisableArea } from "./NonMerchandisableArea";
import { ItemSlot, getItemSlotWidths } from "./itemSlots";
import { useDrawnSqueeze } from "../../common/drawnSqueeze";

const ShelfInner = styled(Flex)<{ height: number; scaleY: number }>`
  width: 100%;
  height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  position: absolute;
  bottom: 0;
  left: 0;
`;

const Items = styled(Flex)<{
  $merchandisableWidth: number;
  $scaleX: number;
  shelfThickness: number;
  scaleY: number;
  align?: "left" | "right" | "between" | "around";
}>`
  position: absolute;
  z-index: 2;
  top: ${({ shelfThickness, scaleY }) => `calc(-${shelfThickness}px * ${scaleY})`};
  left: 0;
  width: ${({ $merchandisableWidth, $scaleX }) => `calc(${$merchandisableWidth}px * ${$scaleX})`};
  display: flex;
  align-items: flex-start;
  flex-grow: 1;
  justify-content: ${({ align }) => {
    if (align === "right") return "flex-end";
    if (align === "left") return "flex-start";
    if (align === "around") return "space-around";
    return "space-between";
  }};

  // First element is dropper, ignore that
  // Second element is always first item in the shelf.
  > *:nth-of-type(2) {
    flex-grow: 0;
  }

  ${({ align }) =>
    (align === "left" || align === "right") &&
    `
    > * {
      flex-grow: 0;
    }
  `}
`;

const ItemsAnchor = styled(Flex)`
  position: absolute;
`;

const ShelfBottoms = styled(Flex)`
  position: relative;
  z-index: 1;
`;

interface Props extends Scaleable {
  currentStoreCode?: string;
  bay: PlanogramBay;
  shelves: PlanogramShelf[];
  bounds: DragBounds;
  isEditable: boolean;
  isPog: boolean;
  highlightedItems: string[];
  pogAtom: PogAtom;
  setIsModified: (modified: boolean) => void;
  availableStores: ResponseMerchFlowApplyFilters["filter_config"] | null;
  pdfMode: boolean;
  customItemLabelOnly?: boolean;
  customLabelRule?: (item: PlanogramItem | UnrangedPlanogramItem) => string | null | undefined;
  canvasItemLabelRule?: CanvasItemLabelRule;
  pdfShelfLabelBayNo?: number;
  bayShift?: number;
}

export const Hangcell = ({
  currentStoreCode,
  bay,
  shelves,
  bounds,
  isEditable,
  isPog,
  highlightedItems,
  scaleX,
  scaleY,
  pogAtom,
  setIsModified,
  availableStores,
  pdfMode,
  customItemLabelOnly,
  customLabelRule,
  canvasItemLabelRule,
  pdfShelfLabelBayNo,
  bayShift = 0,
}: Props) => {
  const pog = useAtomValue(pogAtom);
  const { addItemsToShelf, duplicateItemsToShelf, moveShelf } = usePogAtomActions(pogAtom);

  const setSelectedShelf = useSetAtom(planogramSelectedShelfAtom);
  const [selectedShelfIds, setSelectedShelfIds] = useAtom(planogramSelectedShelvesAtom);
  const { setTab } = usePlanogramPanel();
  const hoveredShelf = useAtomValue(planogramHoveredShelfAtom);
  const showShelfOverflow = useAtomValue(showShelfOverflowAtom);

  const firstShelf = shelves[0];
  const lastShelf = shelves[shelves.length - 1];
  const shelfType = firstShelf.type;
  const shelfHeight = firstShelf.getHeight();
  const shelfThickness = firstShelf.thickness;
  const merchandisableWidth = getMergedMerchandisableWidth(shelves);
  const isSelected = shelves.some((shelf) => selectedShelfIds.includes(shelf.uniqueId));

  const items: PlanogramItem[] = shelves.reduce(
    (items, shelf) => [...items, ...shelf.items],
    [] as PlanogramItem[],
  );
  // An obstruction splits the row: each item gets a fixed slot so none lands on a strip.
  const drawn = useDrawnSqueeze();
  const itemSlotWidths = drawn(items, () => getItemSlotWidths(shelves, items));
  const itemsWidth = itemSlotWidths
    ? shelves.reduce((total, shelf) => total + shelf.width, 0)
    : merchandisableWidth;

  const refItems = useRef<HTMLDivElement>(null);

  const onModify = ({ yBottom }: DragModifications) => {
    setIsModified(true);

    moveShelf(firstShelf, firstShelf.getNewNotchNo(yBottom));
  };

  const onDropOver = () => {
    setIsModified(true);

    onDropOverToShelf({
      refItems,
      onDrop,
    });
  };

  const onDrop = ({ itemIds, mode }: DraggedItemsPayload) => {
    setIsModified(true);

    if (mode === "copy") {
      duplicateItemsToShelf(itemIds, firstShelf);
      return;
    }

    if (!pog) {
      return;
    }

    const draggedItems = resolveDraggedItems(pog, itemIds);

    if (draggedItems.length > 0) {
      addItemsToShelf(draggedItems, firstShelf);
    }
  };

  const setSelected = (event: React.PointerEvent) => {
    const shelf = hoveredShelf || firstShelf;
    const { nextSelectedShelfIds, nextSelectedShelf, nextTab } = calculateShelfSelectionUpdate(
      pog,
      selectedShelfIds,
      shelf,
      event,
    );

    setSelectedShelfIds(nextSelectedShelfIds);
    setSelectedShelf(nextSelectedShelf);
    setTab(nextTab);
  };

  return (
    <Draggable
      uniqueId={firstShelf.uniqueId}
      xLeft={firstShelf.getXLeft() + bayShift}
      xRight={lastShelf.getXRight() + bayShift}
      yBottom={firstShelf.getYBottom()}
      yTop={firstShelf.getYBottom()}
      scaleX={scaleX}
      scaleY={scaleY}
      bounds={{
        ...bounds,
        xLeft: bounds.xLeft + bayShift,
        xRight: bounds.xRight + bayShift,
      }}
      snappables={[]}
      isDraggable={isEditable}
      direction="vertical"
      moveBy={bay.notchSpacing}
      onModify={onModify}
      onClick={setSelected}
    >
      <ShelfInner height={shelfHeight} scaleY={scaleY}>
        {isPog && items.length <= 1 && (
          <Dropper
            side="left"
            width={`${merchandisableWidth * scaleX}px`}
            height={`calc(${shelfHeight}px * ${scaleY})`}
            onDropOver={onDropOver}
            onDrop={onDrop}
            shelfType={shelfType}
          />
        )}

        {!pdfMode && showShelfOverflow && firstShelf.isOverflowing() && (
          <Overflowing shelf={firstShelf} scaleY={scaleY} />
        )}

        <ShelfBottoms>
          {shelves.map((shelf) => (
            <React.Fragment key={shelf.uniqueId}>
              <ShelfBottom
                shelf={shelf}
                scaleX={scaleX}
                scaleY={scaleY}
                isSelected={isSelected}
                pogAtom={pogAtom}
                showShelfName={pdfMode}
                shelfLabelBayNo={pdfShelfLabelBayNo}
                setIsModified={setIsModified}
              />
            </React.Fragment>
          ))}
        </ShelfBottoms>

        <NonMerchandisableArea scaleX={scaleX} scaleY={scaleY} shelves={shelves} />

        <Items
          ref={refItems}
          $merchandisableWidth={itemsWidth}
          $scaleX={scaleX}
          shelfThickness={shelfThickness}
          scaleY={scaleY}
          align={itemSlotWidths ? "left" : firstShelf.align}
        >
          <ItemsAnchor />

          {items.map((item, i) => {
            const renderedItem = (
              <Item
                currentStoreCode={currentStoreCode}
                item={item}
                scaleX={scaleX}
                scaleY={scaleY}
                isEditable={isEditable}
                isHighlighted={highlightedItems.includes(item.uniqueId)}
                isScrolledToView={
                  highlightedItems.length === 1 && highlightedItems.includes(item.uniqueId)
                }
                pogAtom={pogAtom}
                setIsModified={setIsModified}
                availableStores={availableStores}
                pdfMode={pdfMode}
                customItemLabelOnly={customItemLabelOnly}
                customLabelRule={customLabelRule}
                canvasItemLabelRule={canvasItemLabelRule}
              />
            );

            return (
              <React.Fragment key={i}>
                {itemSlotWidths ? (
                  <ItemSlot
                    $align="flex-start"
                    data-item-slot={itemSlotWidths[i]}
                    style={{ width: `${itemSlotWidths[i] * scaleX}px` }}
                  >
                    {renderedItem}
                  </ItemSlot>
                ) : (
                  renderedItem
                )}
              </React.Fragment>
            );
          })}
        </Items>
      </ShelfInner>
    </Draggable>
  );
};
